import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import JupasSurveyAnalyticsSummary from '../components/widgets/JupasSurveyAnalyticsSummary';
import JupasSurveyResponseDetail from '../components/widgets/JupasSurveyResponseDetail';
import { buildPageItems } from '../utils/jupasSurveyResultsDisplay';

const PAGE_SIZE = 20;
const INSTITUTIONS = ['HKU', 'CUHK', 'HKUST', 'CityU', 'Other'];

function formatDateTime(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleString();
  } catch {
    return '';
  }
}

function FilterToggle({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex h-9 items-center rounded-full border px-3.5 text-sm font-semibold transition ${
        active
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'border-edge bg-surface text-ink-muted hover:border-edge-strong hover:bg-surface-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function ResultsPagination({
  t,
  idPrefix,
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  showPagination,
}) {
  const [pageInput, setPageInput] = useState(String(page));

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const pageItems = showPagination ? buildPageItems(page, totalPages) : [];
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    onPageChange(nextPage);
  };

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(page));
      return;
    }
    goToPage(Math.min(Math.max(1, parsed), totalPages));
  };

  const pageButtonClass = (active) =>
    `inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-ink hover:bg-surface-muted'
    }`;

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-edge/80 bg-surface/95 px-4 py-3 shadow-sm backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-ink-subtle">
        {t('jupasSurveyResults.showingRange', { from, to, total })}
      </p>

      {showPagination ? (
        <div className="flex flex-wrap items-center gap-2">
          <nav
            className="flex items-center gap-1"
            aria-label={t('jupasSurveyResults.pageOf', { page, total: totalPages })}
          >
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t('jupasSurveyResults.previousPage')}
            >
              <FaChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </button>
            {pageItems.map((item) =>
              typeof item === 'string' ? (
                <span
                  key={item}
                  className="inline-flex h-8 min-w-8 items-center justify-center px-1 text-sm text-ink-subtle"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => goToPage(item)}
                  className={pageButtonClass(item === page)}
                  aria-current={item === page ? 'page' : undefined}
                >
                  {item}
                </button>
              )
            )}
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t('jupasSurveyResults.nextPage')}
            >
              <FaChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </nav>
          <label className="sr-only" htmlFor={`${idPrefix}-page`}>
            {t('jupasSurveyResults.pageOf', { page, total: totalPages })}
          </label>
          <input
            id={`${idPrefix}-page`}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitPageInput();
              }
            }}
            className="h-8 w-14 rounded-full border border-edge-strong bg-surface px-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      ) : null}
    </div>
  );
}

export default function JupasSurveyResults() {
  const { t } = useTranslation();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [volunteersOnly, setVolunteersOnly] = useState(false);
  const [excludeInternalOnly, setExcludeInternalOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const filterParams = useMemo(
    () => ({
      userId: userIdFilter.trim(),
      institution: institutionFilter.trim(),
      volunteers: volunteersOnly ? '1' : '',
      excludeInternal: excludeInternalOnly ? '1' : '',
    }),
    [userIdFilter, institutionFilter, volunteersOnly, excludeInternalOnly]
  );

  const queryString = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams();
      params.set('year', '2026');
      if (filterParams.userId) params.set('userId', filterParams.userId);
      if (filterParams.institution) params.set('institution', filterParams.institution);
      if (filterParams.volunteers) params.set('volunteers', filterParams.volunteers);
      if (filterParams.excludeInternal) params.set('excludeInternal', filterParams.excludeInternal);
      Object.entries(extra).forEach(([k, v]) => {
        if (v != null && v !== '') params.set(k, String(v));
      });
      return params.toString();
    },
    [filterParams]
  );

  const loadResponses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(`/api/jupas-survey?${queryString({ page, limit: PAGE_SIZE })}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('jupasSurveyResults.loadFailed'));
      setResponses(data.responses || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 1);
      if (page > (data.totalPages || 1)) setPage(data.totalPages || 1);
    } catch (e) {
      setError(e.message || t('jupasSurveyResults.loadFailed'));
      setResponses([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, queryString, t]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await apiClient.get(`/api/jupas-survey/analytics?${queryString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('jupasSurveyResults.loadFailed'));
      setAnalytics(data.analytics || null);
    } catch (e) {
      console.warn('Failed to load JUPAS survey analytics:', e?.message || e);
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [queryString, t]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleUserIdChange = useCallback((value) => {
    setUserIdFilter(value);
    setPage(1);
  }, []);

  const headingCount = analytics?.total != null ? analytics.total : totalCount;
  const showPagination = totalPages > 1;

  return (
    <div className="jupas-survey-home relative min-h-screen overflow-hidden bg-canvas">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-sky-100/85 via-sky-50/35 to-transparent dark:from-sky-950/45 dark:via-sky-950/10 dark:to-transparent"
        aria-hidden
      />
      <div
        className="jupas-survey-home-blob-a pointer-events-none absolute -right-24 top-8 h-72 w-72 rounded-full bg-primary/10 blur-3xl dark:bg-primary/20"
        aria-hidden
      />
      <div
        className="jupas-survey-home-blob-b pointer-events-none absolute -left-24 top-36 h-56 w-56 rounded-full bg-emerald-200/35 blur-3xl dark:bg-emerald-900/20"
        aria-hidden
      />

      <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-2 sm:px-6">
        <header className="px-1 py-8 text-center sm:py-10">
          <div className="relative mx-auto mb-4 flex h-14 w-14 items-center justify-center">
            <span
              className="jupas-survey-home-icon-glow absolute inset-0 rounded-[1.15rem] bg-primary/15 blur-md"
              aria-hidden
            />
            <span className="relative flex h-14 w-14 items-center justify-center rounded-[1.15rem] border border-primary/20 bg-surface/80 text-primary shadow-sm backdrop-blur-sm">
              <FaSearch className="h-6 w-6" aria-hidden />
            </span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            {t('navigation.jupasSurveyResults')}
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            {headingCount === 1
              ? t('jupasSurveyResults.responseSingular', { count: headingCount })
              : t('jupasSurveyResults.responsePlural', { count: headingCount })}
          </p>
          <Link
            to="/jupas-survey"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface/90 px-3 py-1.5 text-xs font-semibold text-link shadow-sm transition hover:border-primary/30 hover:bg-surface"
          >
            {t('jupasSurveyResults.openSurveyForm')}
            <FaExternalLinkAlt className="h-3 w-3" aria-hidden />
          </Link>
        </header>

        <section className="rounded-2xl border border-edge/80 bg-surface/95 p-4 shadow-sm backdrop-blur-sm sm:p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-subtle" htmlFor="jupas-results-search">
                {t('jupasSurveyResults.searchLabel')}
              </label>
              <div className="relative mt-1.5">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" aria-hidden />
                <input
                  id="jupas-results-search"
                  value={userIdFilter}
                  onChange={(e) => handleUserIdChange(e.target.value)}
                  className="h-10 w-full rounded-full border border-edge-strong bg-surface-muted/60 pl-9 pr-4 text-sm shadow-sm outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-ring"
                  placeholder={t('jupasSurveyResults.searchPlaceholder')}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-ink-subtle" htmlFor="jupas-results-institution">
                {t('jupasSurveyResults.institutionLabel')}
              </label>
              <select
                id="jupas-results-institution"
                value={institutionFilter}
                onChange={(e) => {
                  setInstitutionFilter(e.target.value);
                  setPage(1);
                }}
                className="h-10 rounded-full border border-edge-strong bg-surface-muted/60 px-4 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-ring lg:min-w-[12rem]"
              >
                <option value="">{t('jupasSurveyResults.institutionAll')}</option>
                {INSTITUTIONS.map((code) => (
                  <option key={code} value={code}>
                    {t(`jupasSurvey.institutions.${code}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <FilterToggle
              active={volunteersOnly}
              onClick={() => {
                setVolunteersOnly((current) => !current);
                setPage(1);
              }}
            >
              {t('jupasSurveyResults.volunteersOnly')}
            </FilterToggle>
            <FilterToggle
              active={excludeInternalOnly}
              onClick={() => {
                setExcludeInternalOnly((current) => !current);
                setPage(1);
              }}
            >
              {t('jupasSurveyResults.excludeInternalOnly')}
            </FilterToggle>
          </div>

          <p className="mt-3 text-xs leading-relaxed text-ink-subtle">
            {t('jupasSurveyResults.analyticsOverviewHint')}
          </p>
        </section>

        <JupasSurveyAnalyticsSummary analytics={analytics} loading={analyticsLoading} />

        <section className="mt-8">
          <div className="mb-4 flex items-end justify-between gap-3 px-1">
            <div>
              <h2 className="text-xl font-bold text-ink">{t('jupasSurveyResults.responsesHeading')}</h2>
              <p className="mt-0.5 text-sm text-ink-muted">{t('jupasSurveyResults.responsesHint')}</p>
            </div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
              {error}
            </div>
          ) : null}

          <div className="space-y-3">
            <ResultsPagination
              idPrefix="jupas-survey-top"
              t={t}
              page={page}
              totalPages={totalPages}
              total={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              showPagination={showPagination}
            />

            {loading ? (
              <div className="flex justify-center rounded-2xl border border-edge/80 bg-surface/95 px-6 py-16 shadow-sm">
                <LoadingSpinner size="large" text={t('jupasSurveyResults.loading')} />
              </div>
            ) : responses.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-edge bg-surface/70 px-6 py-12 text-center shadow-sm">
                <p className="text-sm text-ink-muted">{t('jupasSurveyResults.emptyState')}</p>
              </div>
            ) : (
              responses.map((row) => {
                const key = row._id || `${row.userId}-${row.updatedAt}`;
                const expanded = expandedKey === key;

                return (
                  <JupasSurveyResponseDetail
                    key={key}
                    row={row}
                    expanded={expanded}
                    onToggle={() => setExpandedKey(expanded ? null : key)}
                    formatDateTime={formatDateTime}
                  />
                );
              })
            )}

            <ResultsPagination
              idPrefix="jupas-survey-bottom"
              t={t}
              page={page}
              totalPages={totalPages}
              total={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              showPagination={showPagination}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
